import { ReactNode, useEffect, useMemo, useState } from 'react';

import { GetUnitsResponseDTO, InspectionDTO, MODULE } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { ColumnFiltersState, RowSelectionState } from '@tanstack/react-table';

import excavatorVector from '@/assets/vectors/excavator.svg';
import manholeShrugVector from '@/assets/vectors/manhole-shrug.svg';
import UndoIcon from '@/components/icons/undo';
import InspectionDrawer from '@/components/inspections/inspection-drawer';
import { TablePageTable } from '@/components/table-page/table-page-table';
import { useModulesStore } from '@/stores/modules.store';

import { columns } from './table-meta';

export const InspectionsTable = ({
  notFoundDisplay,
  data,
  loading,
  onRowSelectionChange,
  rowSelection,
  columnFilters,
  onColumnFiltersChange,
  sorting,
  grouping,
  columnOrder,
  columnVisibility,
  onSortingChange,
  units,
}: {
  notFoundDisplay: ReactNode;
  data: InspectionDTO[];
  loading: boolean;
  onRowSelectionChange: (selectedRows: RowSelectionState) => void;
  rowSelection: RowSelectionState;
  columnFilters: ColumnFiltersState;
  onColumnFiltersChange: (columnFilters: ColumnFiltersState) => void;
  sorting: { key?: string; direction?: 'asc' | 'desc' };
  grouping: string[];
  columnOrder: Array<{ id: string; active: boolean }>;
  columnVisibility: Record<string, boolean>;
  onSortingChange: (sorting: { key?: string; direction?: 'asc' | 'desc' }) => void;
  units: GetUnitsResponseDTO['data'];
}) => {
  const { syncModuleColumns } = useModulesStore();

  const [submission, setSubmission] = useState<InspectionDTO | undefined>(undefined);

  const initialSorting = useMemo(
    () =>
      sorting.key
        ? [
            {
              id: sorting.key,
              desc: sorting.direction === 'desc',
            },
          ]
        : [],
    [sorting.key, sorting.direction],
  );

  useEffect(() => {
    syncModuleColumns(MODULE.Inspections, columns());
  }, [syncModuleColumns]);

  return (
    <>
      <div className="flex-1 bg-white">
        <TablePageTable<InspectionDTO>
          data={data}
          columns={columns(units)}
          notFoundDisplay={notFoundDisplay}
          loading={loading}
          onSortingChange={onSortingChange}
          initialSorting={initialSorting}
          grouping={grouping}
          columnOrder={(columnOrder || []).map((column) => column.id)}
          onRowClick={(row) => setSubmission(row)}
          rowSelection={rowSelection}
          onRowSelectionChange={onRowSelectionChange}
          columnVisibility={columnVisibility}
          columnFilters={columnFilters}
          onColumnFiltersChange={onColumnFiltersChange}
        />
      </div>

      <InspectionDrawer open={!!submission} onOpenChange={() => setSubmission(undefined)} inspectionId={submission?.id} noTrigger />
    </>
  );
};

export const NoInspectionAvailableDisplay = () => {
  return (
    <div className="absolute inset-0 m-auto flex w-[383px] flex-col items-center justify-center gap-[20px]">
      <img src={excavatorVector} alt="Excavator" className="h-[114.5px] w-[220px]" />
      <span className="text-xl leading-[30px] font-medium">No inspections yet</span>
    </div>
  );
};

export const NoInspectionFoundFromSearchDisplay = () => {
  return (
    <div className="absolute inset-0 flex h-full w-full flex-col items-center justify-center gap-4 p-4">
      <img src={manholeShrugVector} alt="Manhole shrug" className="h-28" />
      <span className="text-sm">No inspection activity match your search</span>
    </div>
  );
};

export const NoInspectionFoundFromFiltersDisplay = ({ removeColumnFilters }: { removeColumnFilters: () => void }) => {
  return (
    <div className="bg-grey-1 absolute inset-0 flex h-full w-full flex-col items-center justify-center gap-4 p-4">
      <img src={manholeShrugVector} alt="Manhole shrug" className="h-28" />
      <span className="text-xl font-medium">No inspection activity match your filters</span>
      <span className="text-secondary-text text-sm">Try adjusting your column filters</span>
      <div className="pointer-events-auto flex gap-2">
        <Button variant="grey" onClick={removeColumnFilters} className="gap-1 font-normal">
          <UndoIcon />
          Remove all filters
        </Button>
      </div>
    </div>
  );
};
